import type { StrictPaddingStyle } from "../util/types";

import { Box, type BoxStyles } from "../Box";
import { ErrorText } from "../ErrorText";
import { Label } from "../Label";
import { Text } from "../Text";
import { useTheme } from "../ThemeProvider";
import { useAnimatedNative } from "../util/useAnimatedNative";

import { type ReactNode, useLayoutEffect, useRef, useState, useMemo, useId } from "react";
import { Animated, View, StyleSheet, useWindowDimensions, Platform } from "react-native";

interface ProgressBarProps {
  /**
   * Value of the Progress Bar.
   * The value should be greater than or equal to 0 and less than or equal to 100.
   * If not provided, the progress bar will be in an indeterminate state.
   */
  value?: number;

  /**
   * Optional label text to display for the label.
   */
  labelText?: string;

  /**
   * Optional label of the Progress Bar used for accessibility.
   */
  progressAriaLabel?: string;

  /**
   * Optional explainer text to display above the progress bar in the label.
   */
  explainerText?: string;

  /**
   * Optional descriptive text to display below the progress bar.
   */
  descriptiveText?: string;

  /**
   * Optional error text to display below the progress bar.
   */
  errorText?: string;

  /**
   * Whether to show the percentage value of the progress bar.
   */
  showDataText?: boolean;

  /**
   *  Whether this label is required.
   */
  required?: boolean;

  /**
   * The string contents that should populate the label
   * Optional accessibility explanation to be appended to the 'Required' text for screen readers.
   */
  requiredAccessibilityText?: string;

  /**
   * **Accessibility label for the error icon.** This is read by screen readers to describe the icon's meaning.
   * It should typically be set to a short phrase like **'Error'** or **'Critical'**.
   */
  errorIconAccessibilityLabel?: string;

  /**
   * Icon to display before the label.
   */
  icon?: ReactNode;

  /**
   * Defines the padding style overrides.
   */
  sx?: StrictPaddingStyle;
}

interface DeterminateProgressBarProps {
  value: number;
}

interface IndeterminateProgressBarProps {}

function DeterminateProgressBar({ value }: DeterminateProgressBarProps) {
  const theme = useTheme();

  const styles = useMemo(
    () =>
      StyleSheet.create({
        progressBarContainer: {
          height: 7,
          borderWidth: 1,
          borderColor: theme.colors.borderPrimary,
          borderRadius: theme.borderRadius.full,
          width: "100%",
          overflow: "hidden",
          position: "relative",
          marginBottom: theme.spacing.s2,
        },
        labelContainer: {
          position: "relative",
          marginBottom: theme.spacing.s2,
        },
        percentageContainer: {
          position: "absolute",
          right: 0,
          bottom: 0,
        },
        noLabelPercentageContainer: {
          width: "100%",
          alignItems: "flex-end",
          marginBottom: theme.spacing.s2,
        },
        determinateProgressBar: {
          backgroundColor: theme.colors.backgroundPrimary,
          borderRadius: theme.borderRadius.full,
          width: "100%",
          height: "100%",
          position: "absolute",
        },
        indeterminateContainer: {
          width: "100%",
          height: "100%",
        },
        indeterminateProgressBar: {
          backgroundColor: theme.colors.backgroundPrimary,
          borderRadius: theme.borderRadius.full,
          height: "100%",
          position: "absolute",
        },
      }),
    [theme],
  );

  const animatedValue = useRef(new Animated.Value(value)).current;
  const { width: screenWidth } = useWindowDimensions();
  // Start initially with screen width, this gives a more accurate initial render than 0
  // which will make the progress bar flash with 100%.
  // On layout will update the width to be accurate to the container.
  const [width, setWidth] = useState(screenWidth);
  const useNativeDriver = useAnimatedNative();

  useLayoutEffect(() => {
    animatedValue.stopAnimation(() => {
      const animation = Animated.spring(animatedValue, {
        toValue: value,
        useNativeDriver: useNativeDriver,
        damping: 15,
        stiffness: 100,
      });
      animation.start();
    });
  }, [animatedValue, useNativeDriver, value]);

  const animatedStyle = {
    ...styles.determinateProgressBar,
    transform: [
      {
        translateX: animatedValue.interpolate({
          inputRange: [0, 100],
          outputRange: [-width, 0],
          extrapolate: "clamp",
        }),
      },
    ],
  };

  return (
    <Animated.View
      focusable={false}
      style={animatedStyle}
      aria-hidden={true}
      accessible={false}
      onLayout={(event) => {
        setWidth(event.nativeEvent.layout.width);
      }}
    />
  );
}

function IndeterminateProgressBar({}: IndeterminateProgressBarProps) {
  const animatedValue = useRef(new Animated.Value(0)).current;
  const [containerWidth, setContainerWidth] = useState(0);
  const useNativeDriver = useAnimatedNative();
  const theme = useTheme();

  const styles = useMemo(
    () =>
      StyleSheet.create({
        indeterminateProgressBar: {
          backgroundColor: theme.colors.backgroundPrimary,
          borderRadius: theme.borderRadius.full,
          height: "100%",
          position: "absolute",
        },
        indeterminateContainer: {
          width: "100%",
          height: "100%",
        },
      }),
    [theme],
  );

  useLayoutEffect(() => {
    const createAnimation = () => {
      return Animated.loop(
        Animated.sequence([
          Animated.timing(animatedValue, {
            toValue: 1,
            duration: 1200,
            useNativeDriver: useNativeDriver,
          }),
          Animated.timing(animatedValue, {
            toValue: 0,
            duration: 0,
            useNativeDriver: useNativeDriver,
          }),
        ]),
      );
    };

    const animation = createAnimation();
    animation.start();

    return () => {
      animation.stop();
    };
  }, [animatedValue, useNativeDriver]);

  const indicatorWidth = containerWidth * 0.3;

  const style: BoxStyles = {
    ...styles.indeterminateProgressBar,
    width: indicatorWidth,
    transform: [
      {
        translateX: animatedValue.interpolate({
          inputRange: [0, 1],
          outputRange: [-indicatorWidth, containerWidth + indicatorWidth],
          extrapolate: "clamp",
        }),
      },
      {
        scaleX: animatedValue.interpolate({
          inputRange: [0, 0.4, 1],
          outputRange: [1, 1.25, 1.75],
          extrapolate: "clamp",
        }),
      },
    ],
  };

  return (
    <View
      focusable={false}
      aria-hidden={true}
      accessible={false}
      style={styles.indeterminateContainer}
      onLayout={(event) => {
        setContainerWidth(event.nativeEvent.layout.width);
      }}
    >
      <Animated.View focusable={false} style={style} />
    </View>
  );
}

/**
 * @see https://experience.indeed.design/components/ids/progress-bar
 */
export function ProgressBar({
  value,
  labelText,
  progressAriaLabel,
  explainerText = "",
  descriptiveText,
  errorText,
  showDataText = true,
  required,
  requiredAccessibilityText,
  errorIconAccessibilityLabel,
  icon,
  sx,
}: ProgressBarProps) {
  const theme = useTheme();
  const isIndeterminate = value === undefined;
  const roundedValue = isIndeterminate ? undefined : Math.round(value);
  const progressValueText = isIndeterminate ? undefined : `${roundedValue}%`;
  const requiredText = requiredAccessibilityText || "Required";
  const uniqueId = useId();
  const labelId = `MADS_ProgressBar_label_${uniqueId}`;
  const isWeb = Platform.OS === "web";

  let nativeAccessibilityLabel: string | undefined;

  if (progressAriaLabel && labelText) {
    nativeAccessibilityLabel = `${progressAriaLabel}. ${labelText}${required ? `. ${requiredText}` : ""}`;
  } else if (progressAriaLabel) {
    nativeAccessibilityLabel = progressAriaLabel;
  } else if (labelText) {
    nativeAccessibilityLabel =
      Platform.OS === "ios"
        ? `Progress bar. ${labelText}${required ? `. ${requiredText}` : ""}`
        : `${labelText}${required ? `. ${requiredText}` : ""}`;
  }

  const styles = useMemo(
    () =>
      StyleSheet.create({
        progressBarContainer: {
          height: 7,
          borderWidth: 1,
          borderColor: theme.colors.borderPrimary,
          borderRadius: theme.borderRadius.full,
          width: "100%",
          overflow: "hidden",
          position: "relative",
          marginBottom: theme.spacing.s2,
        },
        labelContainer: {
          position: "relative",
          marginBottom: theme.spacing.s2,
        },
        percentageContainer: {
          position: "absolute",
          right: 0,
          bottom: 0,
        },
        noLabelPercentageContainer: {
          width: "100%",
          alignItems: "flex-end",
          marginBottom: theme.spacing.s2,
        },
        errorContainer: {
          flexDirection: "row",
          alignItems: "center",
          paddingTop: theme.spacing.s1,
        },
        errorIconSpacer: {
          width: theme.spacing.s1,
        },
      }),
    [theme],
  );

  return (
    <>
      {labelText ? (
        <Box sx={styles.labelContainer}>
          <Label
            text={labelText}
            explainerText={explainerText}
            labelID={labelId}
            isRequired={required}
            requiredText={requiredAccessibilityText || "Required"}
            renderStatesToScreenReader={true}
            icon={icon}
          />
          <Box sx={styles.percentageContainer}>
            {!isIndeterminate && showDataText && (
              <Text accessible={false} aria-hidden={true} level={3}>{`${Math.round(value)}%`}</Text>
            )}
          </Box>
        </Box>
      ) : (
        <Box sx={styles.percentageContainer}>
          {!isIndeterminate && showDataText && <Text level={3}>{`${Math.round(value)}%`}</Text>}
        </Box>
      )}
      <Box
        sx={{ ...styles.progressBarContainer, ...sx }}
        {...(isWeb
          ? value !== undefined
            ? {
                role: "progressbar",
                "aria-valuenow": roundedValue,
                "aria-valuemin": 0,
                "aria-valuemax": 100,
              }
            : { role: "img" }
          : {})}
        accessibilityRole="progressbar"
        aria-label={isWeb && !labelText ? progressAriaLabel : nativeAccessibilityLabel}
        aria-labelledby={isWeb && labelText ? labelId : undefined}
        aria-valuetext={isWeb && !isIndeterminate ? progressValueText : undefined}
        accessibilityLabel={nativeAccessibilityLabel}
        accessibilityLabelledBy={
          !isWeb && labelText && !progressAriaLabel && Platform.OS === "android"
            ? labelId
            : undefined
        }
        accessibilityValue={!isWeb && !isIndeterminate ? { text: progressValueText } : undefined}
        accessible={true}
        focusable={false}
      >
        {isIndeterminate ? <IndeterminateProgressBar /> : <DeterminateProgressBar value={value} />}
      </Box>
      {descriptiveText && <Text level={2}>{descriptiveText}</Text>}
      {errorText && (
        <ErrorText errorIconAccessibilityLabel={errorIconAccessibilityLabel} children={errorText} />
      )}
    </>
  );
}
